import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Card,
  CardContent,
  Grid,
  Checkbox,
  TableSortLabel,
  TextField,
  InputAdornment,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Block as IgnoreIcon,
  Replay as ReopenIcon,
  Search as SearchIcon,
  CheckCircleOutline as ClearIcon,
} from '@mui/icons-material';
import { fleetSyncAPI } from '../services/api';

const ConflictsPage = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [conflictGroups, setConflictGroups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('device_imei');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [resolving, setResolving] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVehicles, setSelectedVehicles] = useState({});
  const [selectedGroups, setSelectedGroups] = useState(new Set());

  useEffect(() => {
    fetchConflictVehicles();
    fetchSummary();
  }, [filter, sortBy, sortOrder]);

  useEffect(() => {
    handleSearch();
  }, [searchQuery, conflictGroups]);

  const fetchConflictVehicles = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await fleetSyncAPI.getConflictVehicles(params);
      setConflictGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching conflict vehicles:', error);
      setConflictGroups([]);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await fleetSyncAPI.getConflictsSummary();
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveVehicleConflict = async (vehicleId) => {
    try {
      setResolving(vehicleId);
      await fleetSyncAPI.resolveVehicleConflict({ keep_vehicle_id: vehicleId });
      await fetchConflictVehicles();
      await fetchSummary();
    } catch (error) {
      console.error('Error resolving vehicle conflict:', error);
      alert('Failed to resolve vehicle conflict');
    } finally {
      setResolving(null);
    }
  };

  const handleIgnoreVehicleConflict = async (vehicleId) => {
    try {
      setResolving(vehicleId);
      await fleetSyncAPI.ignoreVehicleConflict(vehicleId);
      await fetchConflictVehicles();
      await fetchSummary();
    } catch (error) {
      console.error('Error ignoring vehicle conflict:', error);
      alert('Failed to ignore vehicle conflict');
    } finally {
      setResolving(null);
    }
  };

  const handleReopenVehicleConflict = async (vehicleId) => {
    try {
      setResolving(vehicleId);
      await fleetSyncAPI.reopenVehicleConflict(vehicleId);
      await fetchConflictVehicles();
      await fetchSummary();
    } catch (error) {
      console.error('Error reopening vehicle conflict:', error);
      alert('Failed to reopen vehicle conflict');
    } finally {
      setResolving(null);
    }
  };

  const handleClearVehicleConflict = async (vehicleId) => {
    try {
      setResolving(vehicleId);
      await fleetSyncAPI.clearVehicleConflict(vehicleId);
      await fetchConflictVehicles();
      await fetchSummary();
    } catch (error) {
      console.error('Error clearing vehicle conflict:', error);
      alert('Failed to clear vehicle conflict');
    } finally {
      setResolving(null);
    }
  };

  const handleSearch = () => {
    // Search is handled client-side since we have all the data
    // The filtering happens in the render logic below
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      resolved: 'success',
      ignored: 'default'
    };
    return colors[status] || 'default';
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Filter and search conflict groups
  const filteredGroups = conflictGroups.filter(group => {
    // Apply search filter
    if (!searchQuery.trim()) {
      return true;
    }

    const query = searchQuery.toLowerCase();
    const imeiMatch = (group.imei || '').toLowerCase().includes(query);
    
    // Check if any vehicle in the group matches
    const vehicleMatch = group.vehicles.some(vehicle => {
      const vehicleNameMatch = (vehicle.name || '').toLowerCase().includes(query);
      const clientNameMatch = (vehicle.client_name || '').toLowerCase().includes(query);
      const clientEmailMatch = (vehicle.client_email || '').toLowerCase().includes(query);
      const serverNameMatch = (vehicle.server_name || '').toLowerCase().includes(query);
      return vehicleNameMatch || clientNameMatch || clientEmailMatch || serverNameMatch;
    });

    return imeiMatch || vehicleMatch;
  });

  // Sort groups
  const sortedGroups = [...filteredGroups].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'device_imei':
        aValue = a.imei || '';
        bValue = b.imei || '';
        break;
      case 'count':
        aValue = a.count;
        bValue = b.count;
        break;
      default:
        aValue = a.imei || '';
        bValue = b.imei || '';
    }

    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedGroups = rowsPerPage > 0
    ? sortedGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedGroups;

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, display: 'flex', justifyContent: 'center', width: '90%', mx: 'auto' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 4, width: '90%', mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Vehicle Conflicts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage vehicles that share the same GPS device
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {selectedGroups.size > 0 && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={async () => {
                try {
                  setResolving('batch');
                  for (const imei of selectedGroups) {
                    const vehicleId = selectedVehicles[imei];
                    if (vehicleId) {
                      await fleetSyncAPI.resolveVehicleConflict({ keep_vehicle_id: vehicleId });
                    }
                  }
                  await fetchConflictVehicles();
                  await fetchSummary();
                  setSelectedGroups(new Set());
                  setSelectedVehicles({});
                } catch (error) {
                  console.error('Error batch resolving:', error);
                  alert('Failed to resolve some conflicts');
                } finally {
                  setResolving(null);
                }
              }}
              disabled={resolving === 'batch'}
            >
              Resolve Selected ({selectedGroups.size})
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchConflictVehicles(); fetchSummary(); }}
          >
            Actualiser
          </Button>
        </Box>
      </Box>

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: isDarkMode ? alpha(theme.palette.error.main, 0.15) : alpha(theme.palette.error.main, 0.1),
              border: `1px solid ${isDarkMode ? alpha(theme.palette.error.main, 0.3) : alpha(theme.palette.error.main, 0.4)}`
            }}>
              <CardContent>
                <Typography color="error.dark" variant="body2" sx={{ fontWeight: 500 }}>
                  Total Conflict Vehicles
                </Typography>
                <Typography variant="h4" sx={{ mt: 1, color: 'error.dark', fontWeight: 600 }}>
                  {summary.conflict_vehicles || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: isDarkMode ? alpha(theme.palette.warning.main, 0.15) : alpha(theme.palette.warning.main, 0.1),
              border: `1px solid ${isDarkMode ? alpha(theme.palette.warning.main, 0.3) : alpha(theme.palette.warning.main, 0.4)}`
            }}>
              <CardContent>
                <Typography color="warning.dark" variant="body2" sx={{ fontWeight: 500 }}>
                  Pending
                </Typography>
                <Typography variant="h4" sx={{ mt: 1, color: 'warning.dark', fontWeight: 600 }}>
                  {summary.pending}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: isDarkMode ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.success.main, 0.1),
              border: `1px solid ${isDarkMode ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.success.main, 0.4)}`
            }}>
              <CardContent>
                <Typography color="success.dark" variant="body2" sx={{ fontWeight: 500 }}>
                  Resolved
                </Typography>
                <Typography variant="h4" sx={{ mt: 1, color: 'success.dark', fontWeight: 600 }}>
                  {summary.resolved}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: isDarkMode ? alpha(theme.palette.grey[500], 0.15) : alpha(theme.palette.grey[500], 0.1),
              border: `1px solid ${isDarkMode ? alpha(theme.palette.grey[500], 0.3) : alpha(theme.palette.grey[500], 0.4)}`
            }}>
              <CardContent>
                <Typography color="text.primary" variant="body2" sx={{ fontWeight: 500 }}>
                  Ignored
                </Typography>
                <Typography variant="h4" sx={{ mt: 1, color: 'text.primary', fontWeight: 600 }}>
                  {summary.ignored}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ 
        mb: 3,
        bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.8) : 'background.paper',
        backgroundImage: 'none'
      }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <TextField
              placeholder="Search by IMEI, vehicle name, or client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={() => setFilter('all')}
                variant={filter === 'all' ? 'contained' : 'outlined'}
                size="small"
              >
                All
              </Button>
              <Button
                onClick={() => setFilter('pending')}
                variant={filter === 'pending' ? 'contained' : 'outlined'}
                color="warning"
                size="small"
              >
                Pending
              </Button>
              <Button
                onClick={() => setFilter('resolved')}
                variant={filter === 'resolved' ? 'contained' : 'outlined'}
                color="success"
                size="small"
              >
                Resolved
              </Button>
              <Button
                onClick={() => setFilter('ignored')}
                variant={filter === 'ignored' ? 'contained' : 'outlined'}
                color="inherit"
                size="small"
              >
                Ignored
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => { fetchConflictVehicles(); fetchSummary(); }}
                size="small"
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </Box>

        <TableContainer>
          {paginatedGroups.length === 0 ? (
            <Box sx={{ p: 8, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No vehicle conflicts found
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead sx={{ bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.1) : 'grey.50' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedGroups.size > 0 && selectedGroups.size < paginatedGroups.length}
                      checked={paginatedGroups.length > 0 && selectedGroups.size === paginatedGroups.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedGroups(new Set(paginatedGroups.map(g => g.imei)));
                        } else {
                          setSelectedGroups(new Set());
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'device_imei'}
                      direction={sortBy === 'device_imei' ? sortOrder : 'asc'}
                      onClick={() => handleSort('device_imei')}
                    >
                      <strong>IMEI</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell><strong>Conflicting Vehicles</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedGroups.map((group) => (
                  <TableRow 
                    key={group.imei}
                    hover
                    selected={selectedGroups.has(group.imei)}
                    sx={{ 
                      '&:hover': { 
                        bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.08) : 'action.hover' 
                      }
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedGroups.has(group.imei)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedGroups);
                          if (e.target.checked) {
                            newSelected.add(group.imei);
                          } else {
                            newSelected.delete(group.imei);
                          }
                          setSelectedGroups(newSelected);
                        }}
                        disabled={!group.vehicles.some(v => v.conflict_status === 'conflict') || !selectedVehicles[group.imei]}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, px: 2, verticalAlign: 'top' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600 }}>
                        {group.imei}
                      </Typography>
                      <Chip 
                        label={`${group.count} vehicles`} 
                        size="small" 
                        color="error"
                        sx={{ mt: 1, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, px: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {group.vehicles.map((vehicle) => (
                          <Box 
                            key={vehicle.id} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              p: 1.5,
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1,
                              bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.5) : 'background.paper'
                            }}
                          >
                            <Checkbox
                              size="small"
                              sx={{ p: 0 }}
                              checked={selectedVehicles[group.imei] === vehicle.id}
                              onChange={() => {
                                setSelectedVehicles(prev => ({
                                  ...prev,
                                  [group.imei]: vehicle.id
                                }));
                                // Auto-select the group when a vehicle is selected
                                if (group.vehicles.some(v => v.conflict_status === 'conflict')) {
                                  setSelectedGroups(prev => {
                                    const newSelected = new Set(prev);
                                    newSelected.add(group.imei);
                                    return newSelected;
                                  });
                                }
                              }}
                            />
                            <Box sx={{ flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {vehicle.name}
                                </Typography>
                                {vehicle.conflict_status && vehicle.conflict_status !== 'conflict' && (
                                  <Chip
                                    label={vehicle.conflict_status}
                                    color={getStatusColor(vehicle.conflict_status)}
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                                {vehicle.client_name && (
                                  <Typography variant="caption" color="text.secondary">
                                    Client: {vehicle.client_name}
                                  </Typography>
                                )}
                                {vehicle.server_name && (
                                  <Chip 
                                    label={vehicle.server_name} 
                                    size="small" 
                                    color="info"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                  />
                                )}
                                {vehicle.registration_number && (
                                  <Typography variant="caption" color="text.secondary">
                                    Reg: {vehicle.registration_number}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 2, px: 2, verticalAlign: 'top' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {group.vehicles.some(v => v.conflict_status === 'conflict') ? (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<CheckIcon />}
                              disabled={resolving === group.imei || !selectedVehicles[group.imei]}
                              onClick={async () => {
                                if (!selectedVehicles[group.imei]) {
                                  alert('Please select a vehicle to keep');
                                  return;
                                }
                                try {
                                  setResolving(group.imei);
                                  await fleetSyncAPI.resolveVehicleConflict({ keep_vehicle_id: selectedVehicles[group.imei] });
                                  await fetchConflictVehicles();
                                  await fetchSummary();
                                  setSelectedVehicles(prev => {
                                    const newState = { ...prev };
                                    delete newState[group.imei];
                                    return newState;
                                  });
                                } catch (error) {
                                  console.error('Error resolving conflict:', error);
                                  alert('Failed to resolve conflict');
                                } finally {
                                  setResolving(null);
                                }
                              }}
                            >
                              Resolve
                            </Button>
                            <Button
                              variant="outlined"
                              color="inherit"
                              size="small"
                              startIcon={<IgnoreIcon />}
                              disabled={resolving === group.imei}
                              onClick={async () => {
                                try {
                                  setResolving(group.imei);
                                  for (const vehicle of group.vehicles) {
                                    await fleetSyncAPI.ignoreVehicleConflict(vehicle.id);
                                  }
                                  await fetchConflictVehicles();
                                  await fetchSummary();
                                  setSelectedVehicles(prev => {
                                    const newState = { ...prev };
                                    delete newState[group.imei];
                                    return newState;
                                  });
                                } catch (error) {
                                  console.error('Error ignoring conflicts:', error);
                                  alert('Failed to ignore conflicts');
                                } finally {
                                  setResolving(null);
                                }
                              }}
                            >
                              Ignore All
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            startIcon={<ReopenIcon />}
                            disabled={resolving === group.imei}
                            onClick={async () => {
                              try {
                                setResolving(group.imei);
                                console.log('Reopening vehicles in group:', group.imei);
                                console.log('Vehicles to reopen:', group.vehicles.map(v => ({ id: v.id, name: v.name })));
                                
                                for (const vehicle of group.vehicles) {
                                  console.log(`Reopening vehicle ${vehicle.id}: ${vehicle.name}`);
                                  try {
                                    await fleetSyncAPI.reopenVehicleConflict(vehicle.id);
                                    console.log(`Successfully reopened vehicle ${vehicle.id}`);
                                  } catch (vehicleError) {
                                    console.error(`Failed to reopen vehicle ${vehicle.id}:`, vehicleError);
                                    console.error('Error details:', vehicleError.response?.data);
                                    throw vehicleError;
                                  }
                                }
                                
                                console.log('All vehicles reopened, refreshing data...');
                                await fetchConflictVehicles();
                                await fetchSummary();
                                console.log('Data refreshed successfully');
                              } catch (error) {
                                console.error('Error reopening conflicts:', error);
                                console.error('Full error:', error.response?.data || error.message);
                                alert(`Failed to reopen conflicts: ${error.response?.data?.error || error.message}`);
                              } finally {
                                setResolving(null);
                              }
                            }}
                          >
                            Reopen All
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>

      <TablePagination
        component="div"
        count={sortedGroups.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
        labelRowsPerPage="Lignes par page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Container>
  );
};

export default ConflictsPage;
